import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { RequirePermission } from "@/components/auth/RequirePermission";
import { usePowtoonAuth } from "@/lib/auth/PowtoonAuthContext";
import { getOrgDomain, setOrgDomain } from "@/lib/powtoon-oauth";
import { setAuthIntent } from "@/lib/auth/postLoginDestination";
import { powtoonBE } from "@/lib/powtoon-be/client";
import { downloadScormPackage } from "@/lib/scorm/downloadScormPackage";
import { Button } from "@/components/ui/button";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

type State =
  | { kind: "working" }
  | { kind: "done" }
  | { kind: "error"; message: string };

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen grid place-items-center bg-background px-6">
      <div className="max-w-md text-center">{children}</div>
    </main>
  );
}

function ScormRunner({
  projectId,
  version,
  scenarioName,
}: {
  projectId: string;
  version: "1.2" | "2004";
  scenarioName: string;
}) {
  const [state, setState] = useState<State>({ kind: "working" });
  const startedRef = useRef(false);

  const run = async () => {
    setState({ kind: "working" });
    try {
      const { public_link_token } = await powtoonBE.scenarios.createPublicLinkToken(projectId);
      await downloadScormPackage({
        scenarioId: projectId,
        scenarioName,
        publicLinkToken: public_link_token,
        version,
      });
      setState({ kind: "done" });
    } catch (e) {
      setState({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    }
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (state.kind === "working") {
    return (
      <Shell>
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
        <h1 className="mt-4 font-display text-xl font-semibold">
          Preparing your SCORM {version} package…
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          This usually takes a few seconds.
        </p>
      </Shell>
    );
  }
  if (state.kind === "done") {
    return (
      <Shell>
        <CheckCircle2 className="mx-auto h-8 w-8 text-primary" />
        <h1 className="mt-4 font-display text-xl font-semibold">Download started</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Your SCORM {version} package has downloaded. You can close this tab.
        </p>
        <Button className="mt-6" variant="secondary" onClick={run}>
          Download again
        </Button>
      </Shell>
    );
  }
  return (
    <Shell>
      <AlertCircle className="mx-auto h-8 w-8 text-destructive" />
      <h1 className="mt-4 font-display text-xl font-semibold text-destructive">
        SCORM download failed
      </h1>
      <p className="mt-2 break-words text-sm text-muted-foreground">{state.message}</p>
      <Button className="mt-6" onClick={run}>
        Retry
      </Button>
    </Shell>
  );
}

export function LearnerScormDownload() {
  useDocumentMeta({ title: "Downloading SCORM package — Powtoon Live Avatar" });
  const { projectId = "" } = useParams();
  const [params] = useSearchParams();
  const rawVersion = params.get("scormVersion") ?? params.get("version");
  const orgDomainParam = params.get("orgDomain");
  const scenarioName = (params.get("scenarioName") ?? "Live Avatar").trim() || "Live Avatar";

  const version: "1.2" | "2004" | null =
    rawVersion === "1.2" || rawVersion === "2004" ? rawVersion : null;

  const {
    isLoading,
    isAuthenticated,
    orgDomain,
    signInWithPowtoon,
    signOut,
  } = usePowtoonAuth();
  const [tenantSwitching, setTenantSwitching] = useState(false);

  // Persist orgDomain + intent so OAuth round-trip returns here.
  useEffect(() => {
    if (!projectId || !version) return;
    if (orgDomainParam) setOrgDomain(orgDomainParam);
    setAuthIntent({
      kind: "learner-scorm",
      projectId,
      version,
      scenarioName,
      orgDomain: orgDomainParam ?? undefined,
    });
  }, [projectId, version, orgDomainParam, scenarioName]);

  // Tenant mismatch — force re-auth against the requested org.
  useEffect(() => {
    if (isLoading || !isAuthenticated || !orgDomainParam) return;
    const stored = getOrgDomain();
    if (stored && stored !== orgDomainParam) {
      setTenantSwitching(true);
      signOut();
      signInWithPowtoon();
    }
  }, [isLoading, isAuthenticated, orgDomainParam, signOut, signInWithPowtoon]);

  // Kick off OAuth if not authenticated.
  useEffect(() => {
    if (isLoading || isAuthenticated || tenantSwitching) return;
    signInWithPowtoon();
  }, [isLoading, isAuthenticated, tenantSwitching, signInWithPowtoon]);

  const showLoader = useMemo(
    () => isLoading || tenantSwitching || !isAuthenticated,
    [isLoading, tenantSwitching, isAuthenticated],
  );

  if (!projectId || !version) {
    return (
      <Shell>
        <AlertCircle className="mx-auto h-8 w-8 text-destructive" />
        <h1 className="mt-4 font-display text-xl font-semibold text-destructive">
          Invalid SCORM link
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          The link is missing a valid <code>scormVersion</code> (must be{" "}
          <code>1.2</code> or <code>2004</code>) or a project id.
        </p>
      </Shell>
    );
  }

  if (showLoader) {
    return (
      <Shell>
        <Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" />
        <h1 className="mt-4 font-display text-xl font-semibold">Signing you in…</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          We'll start the SCORM download as soon as you're authenticated.
        </p>
      </Shell>
    );
  }

  return (
    <RequirePermission permission="live-avatars.view">
      <ScormRunner projectId={projectId} version={version} scenarioName={scenarioName} />
    </RequirePermission>
  );
}